// Hotel Setup — Guest Communication composer + activity (Phase D) and
// Phase 2A.1 Community Board (Condominium). Reuses the EXISTING voucher engine,
// hotel_room_messages, dispatch-time translate_text and hotel_feedback_photos.
// Targeting via existing hotel_rooms registry. No second engine.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Send, MailOpen, Gift, MessageSquare, Megaphone, ImagePlus, X } from 'lucide-react';

const fmtWhen = (s) => {
    try {
        return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return s || '—'; }
};

export const AssistantComm = ({ cardId, enabled, propertyType }) => {
    const { t } = useTranslation();
    const isCondo = propertyType === 'condominium_association';
    const [mode, setMode] = useState('message');   // message | voucher | community
    const [rooms, setRooms] = useState('');
    const [floors, setFloors] = useState('');
    const [targetMode, setTargetMode] = useState('units'); // units | floor | all (community)
    const [allRooms, setAllRooms] = useState(false);
    const [priority, setPriority] = useState('normal');
    const [audience, setAudience] = useState('guest');
    const [activeHours, setActiveHours] = useState('24');
    const [communityDays, setCommunityDays] = useState('30');
    const [openBatch, setOpenBatch] = useState(null);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [value, setValue] = useState('');
    const [expiry, setExpiry] = useState('30');
    const [sending, setSending] = useState(false);
    const [batches, setBatches] = useState([]);
    // Community Board
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState('');
    const [requireAck, setRequireAck] = useState(false);
    const [translate, setTranslate] = useState(true);
    const [photoIds, setPhotoIds] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = useCallback(() => {
        if (!cardId) return;
        api.get(`/assistant/admin/communications?card_id=${cardId}`)
            .then((r) => setBatches(r.data?.batches || []))
            .catch(() => {});
    }, [cardId]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!isCondo) return;
        api.get('/assistant/admin/property-defaults/condominium_association')
            .then((r) => setCategories(r.data?.notice_categories || []))
            .catch(() => {});
    }, [isCondo]);

    if (!enabled) return null;

    const catObj = categories.find((c) => c.id === category);
    const catLabels = catObj ? { en: catObj.label_en, fr: catObj.label_fr, es: catObj.label_es } : null;

    const pickPhotos = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        try {
            const fd = new FormData();
            Array.from(files).slice(0, 3).forEach((f) => fd.append('photos', f));
            const r = await api.post(`/assistant/admin/notice-photos?card_id=${cardId}`, fd);
            setPhotoIds((prev) => [...prev, ...(r.data?.photo_ids || [])].slice(0, 3));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('assistant.commPhotoFailed', 'Could not upload photo'));
        } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    const send = async () => {
        setSending(true);
        try {
            if (effMode === 'community') {
                await api.post('/assistant/admin/communications', {
                    card_id: cardId,
                    channel: 'community_board',
                    type: 'message',
                    category, category_labels: catLabels,
                    rooms: targetMode === 'units' ? rooms.split(',').map((s) => s.trim()).filter(Boolean) : [],
                    floors: targetMode === 'floor' ? floors.split(',').map((s) => s.trim()).filter(Boolean) : [],
                    all_rooms: targetMode === 'all',
                    require_ack: requireAck,
                    translate,
                    source_language: 'en',
                    photo_ids: photoIds,
                    priority: 'normal',
                    active_hours: (parseInt(communityDays) || 30) * 24,
                    title: title.trim() || null,
                    message: message.trim(),
                });
            } else {
                await api.post('/assistant/admin/communications', {
                    card_id: cardId,
                    rooms: allRooms ? [] : rooms.split(',').map((s) => s.trim()).filter(Boolean),
                    all_rooms: allRooms,
                    type: mode,
                    priority,
                    audience: mode === 'voucher' ? 'guest' : audience,
                    active_hours: parseInt(activeHours) || 24,
                    title: title.trim() || null,
                    message: message.trim(),
                    voucher_value: mode === 'voucher' ? parseFloat(value) || 0 : null,
                    voucher_expiry_days: parseInt(expiry) || 30,
                });
            }
            toast.success(t('assistant.commSent', 'Communication sent'));
            setMessage(''); setTitle(''); setValue(''); setPhotoIds([]); setCategory('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('assistant.commFailed', 'Could not send'));
        } finally { setSending(false); }
    };

    const communityDisabled = sending || !category || !message.trim()
        || (targetMode === 'units' && !rooms.trim()) || (targetMode === 'floor' && !floors.trim());
    const standardDisabled = sending || (!allRooms && !rooms.trim())
        || (mode === 'message' && !message.trim()) || (mode === 'voucher' && !value);
    // Condo management uses the Community Notice composer only — no hotel-style
    // Message/Voucher (room/guest/stay/voucher terminology has no meaning for a condo).
    const effMode = isCondo ? 'community' : mode;

    return (
        <div className="border rounded-lg p-3 space-y-3" data-testid="assist-comm-panel">
            <p className="text-sm font-semibold">
                {isCondo ? t('assistant.commTitleCondo', 'Send a notice or message to units') : t('assistant.commTitle', 'Send a message or voucher to rooms')}
            </p>

            {/* Mode selector — hidden for condo (Community Notice only) */}
            {!isCondo && (
            <div className="flex gap-2">
                {isCondo && (
                    <button type="button" onClick={() => setMode('community')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-colors ${mode === 'community' ? 'bg-sky-600 text-white border-sky-600' : 'bg-background border-input'}`}
                        data-testid="comm-mode-community">
                        <Megaphone className="w-3.5 h-3.5" />{t('assistant.commCommunity', 'Community Notice')}
                    </button>
                )}
                {['message', 'voucher'].map((k) => (
                    <button key={k} type="button" onClick={() => setMode(k)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-colors ${mode === k ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-background border-input'}`}
                        data-testid={`comm-type-${k}`}>
                        {k === 'message' ? <MessageSquare className="w-3.5 h-3.5" /> : <Gift className="w-3.5 h-3.5" />}
                        {k === 'message' ? t('assistant.commMessage', 'Message') : t('assistant.commVoucher', 'Voucher')}
                    </button>
                ))}
            </div>
            )}

            {effMode === 'community' ? (
                <>
                    {/* Category */}
                    <div className="space-y-1">
                        <p className="text-xs font-semibold">{t('assistant.commCategory', 'Category')}</p>
                        <select value={category} onChange={(e) => setCategory(e.target.value)}
                            className="w-full border rounded-lg px-2 py-2 text-xs bg-background" data-testid="comm-category-select">
                            <option value="">{t('assistant.commCategoryPick', 'Select a category…')}</option>
                            {categories.map((c) => (
                                <option key={c.id} value={c.id}>{c.label_en}</option>
                            ))}
                        </select>
                    </div>
                    {/* Targeting */}
                    <div className="space-y-1.5">
                        <p className="text-xs font-semibold">{t('assistant.commTarget', 'Send to')}</p>
                        <div className="flex gap-2">
                            {[
                                { k: 'all', label: t('assistant.commTargetAll', 'Entire property') },
                                { k: 'floor', label: t('assistant.commTargetFloor', 'By floor') },
                                { k: 'units', label: t('assistant.commTargetUnits', 'Specific unit(s)') },
                            ].map((o) => (
                                <button key={o.k} type="button" onClick={() => setTargetMode(o.k)}
                                    className={`flex-1 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${targetMode === o.k ? 'bg-sky-600 text-white border-sky-600' : 'bg-background border-input'}`}
                                    data-testid={`comm-target-${o.k}`}>{o.label}</button>
                            ))}
                        </div>
                        {targetMode === 'floor' && (
                            <Input value={floors} onChange={(e) => setFloors(e.target.value)}
                                placeholder={t('assistant.commFloors', 'Floor(s), comma-separated (2, 3)')}
                                data-testid="comm-floors-input" className="text-xs" />
                        )}
                        {targetMode === 'units' && (
                            <Input value={rooms} onChange={(e) => setRooms(e.target.value)}
                                placeholder={t('assistant.commUnits', 'Unit numbers, comma-separated (105, 205)')}
                                data-testid="comm-rooms-input" className="text-xs" />
                        )}
                    </div>
                    <Input value={title} onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('assistant.commTitleField', 'Title (optional)')} data-testid="comm-title-input" className="text-xs" />
                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={1000}
                        placeholder={t('assistant.commNoticePh', 'e.g. Water will be shut off Tuesday 9am–12pm for maintenance.')}
                        className="w-full border rounded-lg p-2 text-xs bg-background" data-testid="comm-message-input" />
                    {/* Photo */}
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                                onChange={(e) => pickPhotos(e.target.files)} data-testid="comm-photo-input" />
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || photoIds.length >= 3}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-input bg-background hover:bg-muted/40 disabled:opacity-50"
                                data-testid="comm-photo-btn">
                                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                                {t('assistant.commAddPhoto', 'Add photo (optional)')}
                            </button>
                        </div>
                        {photoIds.length > 0 && (
                            <div className="flex flex-wrap gap-2" data-testid="comm-photo-chips">
                                {photoIds.map((pid) => (
                                    <span key={pid} className="inline-flex items-center gap-1 text-[11px] bg-muted rounded px-2 py-1" data-testid={`comm-photo-chip-${pid}`}>
                                        {t('assistant.commPhoto', 'Photo')}
                                        <button type="button" onClick={() => setPhotoIds((p) => p.filter((x) => x !== pid))}><X className="w-3 h-3" /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Options */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="comm-require-ack-label">
                            <input type="checkbox" checked={requireAck} onChange={(e) => setRequireAck(e.target.checked)} data-testid="comm-require-ack" />
                            {t('assistant.commRequireAck', 'Require acknowledgement')}
                        </label>
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="comm-translate-label">
                            <input type="checkbox" checked={translate} onChange={(e) => setTranslate(e.target.checked)} data-testid="comm-translate" />
                            {t('assistant.commTranslate', 'Auto-translate (EN/FR/ES)')}
                        </label>
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] text-muted-foreground whitespace-nowrap">{t('assistant.commActiveFor', 'Active for')}</span>
                            <select value={communityDays} onChange={(e) => setCommunityDays(e.target.value)}
                                className="border rounded-lg px-2 py-1.5 text-xs bg-background" data-testid="comm-community-days">
                                <option value="7">{t('assistant.commDays7', '7 days')}</option>
                                <option value="30">{t('assistant.commDays30', '30 days')}</option>
                                <option value="60">{t('assistant.commDays60', '60 days')}</option>
                                <option value="90">{t('assistant.commDays90', '90 days')}</option>
                            </select>
                        </div>
                    </div>
                    <Button size="sm" onClick={send} disabled={communityDisabled} className="w-full" data-testid="comm-send-btn">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        {t('assistant.commPublish', 'Publish notice')}
                    </Button>
                </>
            ) : (
                <>
                    <div className="flex gap-2 items-center">
                        <Input value={rooms} onChange={(e) => setRooms(e.target.value)} disabled={allRooms}
                            placeholder={t('assistant.commRooms', 'Room numbers, comma-separated (105, 205)')}
                            data-testid="comm-rooms-input" className="text-xs" />
                        <label className="flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer">
                            <input type="checkbox" checked={allRooms} onChange={(e) => setAllRooms(e.target.checked)}
                                data-testid="comm-all-rooms" />
                            {t('assistant.allRooms', 'All rooms')}
                        </label>
                    </div>
                    <Input value={title} onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('assistant.commTitleField', 'Title (optional)')} data-testid="comm-title-input" className="text-xs" />
                    <div className="space-y-1.5">
                        <p className="text-xs font-semibold">{t('assistant.commPriorityTitle', 'Message priority')}</p>
                        {[
                            { k: 'priority', dot: 'bg-rose-500', label: t('assistant.commPriority', 'Priority'),
                              hint: t('assistant.commPriorityHint', 'Guest must read and acknowledge this message before continuing. It will appear immediately when they tap into YulOnTap.') },
                            { k: 'normal', dot: 'bg-slate-300', label: t('assistant.commNormal', 'Normal'),
                              hint: t('assistant.commNormalHint', 'Message appears in Hotel Messages with an unread indicator. It will not interrupt the guest experience.') },
                        ].map((o) => (
                            <button key={o.k} type="button" onClick={() => setPriority(o.k)}
                                className={`w-full text-left border rounded-lg p-2.5 transition-colors ${priority === o.k
                                    ? (o.k === 'priority' ? 'border-rose-500 bg-rose-500/5' : 'border-emerald-600 bg-emerald-500/5')
                                    : 'border-input hover:bg-muted/40'}`}
                                data-testid={`comm-priority-${o.k}`}>
                                <span className="flex items-center gap-2 text-xs font-semibold">
                                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${o.dot}`} /> {o.label}
                                    {priority === o.k && <span className="ml-auto text-[10px] font-bold">✓</span>}
                                </span>
                                <span className="block text-[11px] text-muted-foreground mt-1">{o.hint}</span>
                            </button>
                        ))}
                    </div>
                    {mode === 'message' && (
                        <div className="space-y-1.5">
                            <p className="text-xs font-semibold">{t('assistant.commAudienceTitle', 'Who is this message for?')}</p>
                            {[
                                { k: 'guest', dot: 'bg-emerald-500', label: t('assistant.commAudienceGuest', 'Current guest only'),
                                  hint: t('assistant.commAudienceGuestHint', 'Private to this guest. It disappears automatically when their stay ends.') },
                                { k: 'hotel', dot: 'bg-sky-500', label: t('assistant.commAudienceHotel', 'Hotel notice (whole room)'),
                                  hint: t('assistant.commAudienceHotelHint', 'Stays on the room even if a new guest checks in — until it expires. Example: "Pool closed for maintenance."') },
                            ].map((o) => (
                                <button key={o.k} type="button" onClick={() => setAudience(o.k)}
                                    className={`w-full text-left border rounded-lg p-2.5 transition-colors ${audience === o.k
                                        ? (o.k === 'hotel' ? 'border-sky-500 bg-sky-500/5' : 'border-emerald-600 bg-emerald-500/5')
                                        : 'border-input hover:bg-muted/40'}`}
                                    data-testid={`comm-audience-${o.k}`}>
                                    <span className="flex items-center gap-2 text-xs font-semibold">
                                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${o.dot}`} /> {o.label}
                                        {audience === o.k && <span className="ml-auto text-[10px] font-bold">✓</span>}
                                    </span>
                                    <span className="block text-[11px] text-muted-foreground mt-1">{o.hint}</span>
                                </button>
                            ))}
                            {audience === 'hotel' && (
                                <div className="flex items-center gap-2 pl-1">
                                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">{t('assistant.commActiveFor', 'Active for')}</span>
                                    <select value={activeHours} onChange={(e) => setActiveHours(e.target.value)}
                                        className="border rounded-lg px-2 py-1.5 text-xs bg-background" data-testid="comm-active-hours">
                                        <option value="4">{t('assistant.commHours4', '4 hours')}</option>
                                        <option value="12">{t('assistant.commHours12', '12 hours')}</option>
                                        <option value="24">{t('assistant.commHours24', '1 day')}</option>
                                        <option value="48">{t('assistant.commHours48', '2 days')}</option>
                                        <option value="72">{t('assistant.commHours72', '3 days')}</option>
                                        <option value="168">{t('assistant.commHours168', '7 days')}</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    )}
                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} maxLength={1000}
                        placeholder={mode === 'voucher'
                            ? t('assistant.commVoucherMsg', 'Voucher message (e.g. Enjoy a complimentary $20 Spa Gift)')
                            : t('assistant.commMsgPh', 'e.g. Free continental breakfast for you today.')}
                        className="w-full border rounded-lg p-2 text-xs bg-background" data-testid="comm-message-input" />
                    {mode === 'voucher' && (
                        <div className="flex gap-2">
                            <Input type="number" value={value} onChange={(e) => setValue(e.target.value)}
                                placeholder={t('assistant.commValue', 'Value ($)')} data-testid="comm-value-input" className="text-xs" />
                            <Input type="number" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                                placeholder={t('assistant.commExpiry', 'Valid days')} data-testid="comm-expiry-input" className="text-xs" />
                        </div>
                    )}
                    <Button size="sm" onClick={send} disabled={standardDisabled} className="w-full" data-testid="comm-send-btn">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        {t('assistant.commSend', 'Send')}
                    </Button>
                </>
            )}

            {batches.length > 0 && (
                <div className="space-y-2" data-testid="comm-batches">
                    <p className="text-xs font-semibold text-muted-foreground">{t('assistant.commActivity', 'Activity')}</p>
                    {batches.slice(0, 10).map((b) => (
                        <div key={b.batch_id} className="border rounded-lg p-2 text-xs" data-testid={`comm-batch-${b.batch_id}`}>
                            <button type="button" className="w-full flex items-center gap-2 flex-wrap text-left"
                                onClick={() => setOpenBatch(openBatch === b.batch_id ? null : b.batch_id)}
                                data-testid={`comm-batch-toggle-${b.batch_id}`}>
                                {b.channel === 'community_board' ? <Megaphone className="w-3.5 h-3.5 text-sky-500" />
                                    : b.type === 'voucher' ? <Gift className="w-3.5 h-3.5 text-amber-500" /> : <MailOpen className="w-3.5 h-3.5 text-sky-500" />}
                                {b.channel === 'community_board' && b.category_labels && (
                                    <span className="text-[10px] font-bold uppercase text-sky-600 bg-sky-500/10 border border-sky-300 rounded-full px-1.5"
                                        data-testid={`comm-batch-category-${b.batch_id}`}>{b.category_labels.en || b.category}</span>
                                )}
                                {b.priority === 'priority' && (
                                    <span className="text-[10px] font-bold uppercase text-rose-600 bg-rose-500/10 border border-rose-300 rounded-full px-1.5"
                                        data-testid={`comm-batch-priority-${b.batch_id}`}>{t('assistant.commPriority', 'Priority')}</span>
                                )}
                                {b.require_ack && (
                                    <span className="text-[10px] font-bold uppercase text-emerald-600 bg-emerald-500/10 border border-emerald-300 rounded-full px-1.5"
                                        data-testid={`comm-batch-ack-${b.batch_id}`}>{t('assistant.commAckRequired', 'Ack')}</span>
                                )}
                                {b.audience === 'hotel' && b.channel !== 'community_board' && (
                                    <span className="text-[10px] font-bold uppercase text-sky-600 bg-sky-500/10 border border-sky-300 rounded-full px-1.5"
                                        data-testid={`comm-batch-hotel-${b.batch_id}`}>
                                        {t('assistant.commHotelBadge', 'Hotel notice')}
                                        {b.active_until && <> · {t('assistant.commUntil', 'until')} {fmtWhen(b.active_until)}</>}
                                    </span>
                                )}
                                <span className="font-medium truncate max-w-[180px]">{b.title || b.message || b.type}</span>
                                <span className="text-muted-foreground ml-auto">
                                    {b.rooms.length} {t('assistant.commRoomsN', 'room(s)')} · {t('assistant.commDelivered', 'delivered')} {b.delivered} · {t('assistant.commOpened', 'opened')} {b.opened}
                                    {(b.priority === 'priority' || b.require_ack) && <> · {t('assistant.commAcked', 'acknowledged')} {b.acknowledged}</>}
                                    {b.type === 'voucher' && <> · {t('assistant.commRedeemed', 'redeemed')} {b.redeemed}</>}
                                </span>
                            </button>
                            {openBatch === b.batch_id && (
                                <div className="mt-2 space-y-1" data-testid={`comm-batch-items-${b.batch_id}`}>
                                    {(b.items || []).map((it, i) => (
                                        <div key={i} className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground border-t pt-1">
                                            <span className="font-medium text-foreground">{t('assistant.commRoom', 'Room')} {it.room_number}</span>
                                            <span>{t('assistant.commDeliveredAt', 'Delivered')}: {fmtWhen(it.delivered_at)}</span>
                                            <span>{t('assistant.commSeenAt', 'Seen')}: {it.opened_at ? fmtWhen(it.opened_at) : '—'}</span>
                                            {(b.priority === 'priority' || b.require_ack) && (
                                                <span data-testid={`comm-item-ack-${b.batch_id}-${it.room_number}`}>
                                                    {t('assistant.commAckedAt', 'Acknowledged')}: {it.acknowledged_at ? fmtWhen(it.acknowledged_at) : '—'}
                                                </span>
                                            )}
                                            {b.type === 'voucher' && <span>{t('assistant.commRedeemed', 'redeemed')}: {it.redeemed ? '✓' : '—'}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AssistantComm;
